import { useState, useEffect, useRef } from 'react';
import { ambientesDisponibles, type AmbienteDisponible } from '@/data/ambientes';

// Componente de imagen lazy
const LazyImage = ({ src, alt, className, onLoad }: { src: string; alt: string; className: string; onLoad?: () => void }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const image = entry.target as HTMLImageElement;
            image.src = image.dataset.src || '';
            observer.unobserve(image);
          }
        });
      },
      {
        rootMargin: '50px',
      }
    );

    observer.observe(img);

    return () => {
      if (img) observer.unobserve(img);
    };
  }, []);

  return (
    <>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      <img
        ref={imgRef}
        data-src={src}
        alt={alt}
        className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => {
          setIsLoaded(true);
          onLoad?.();
        }}
        style={{ transition: 'opacity 0.3s ease-in-out' }}
      />
    </>
  );
};

export default function AmbientesDisponiblesSection() {
  const [ambienteAmpliado, setAmbienteAmpliado] = useState<AmbienteDisponible | null>(null);
  const [imagenesHover, setImagenesHover] = useState<{ [key: number]: boolean }>({});
  const [indiceCarrusel, setIndiceCarrusel] = useState(0);
  const [ambientesMostrados, setAmbientesMostrados] = useState(10);
  const [hotspotActivo, setHotspotActivo] = useState<string | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  
  // Estados para zoom
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  const abrirAmbienteAmpliado = (ambiente: AmbienteDisponible) => {
    setAmbienteAmpliado(ambiente);
    setIndiceCarrusel(0);
    setHotspotActivo(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    document.body.style.overflow = 'hidden';
  };

  const cerrarAmbienteAmpliado = () => {
    setAmbienteAmpliado(null);
    setIndiceCarrusel(0);
    setHotspotActivo(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    document.body.style.overflow = 'unset';
  };

  const manejarHover = (ambienteId: number, isHovering: boolean) => {
    if (window.innerWidth > 768) {
      setImagenesHover(prev => ({
        ...prev,
        [ambienteId]: isHovering
      }));
    }
  };

  const cargarMasAmbientes = () => {
    setAmbientesMostrados(prev => Math.min(prev + 6, ambientesDisponibles.length));
  };

  const siguienteImagen = () => {
    if (ambienteAmpliado?.imagenSecundaria) {
      setIndiceCarrusel((prev) => (prev + 1) % 2);
      setHotspotActivo(null);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const anteriorImagen = () => {
    if (ambienteAmpliado?.imagenSecundaria) {
      setIndiceCarrusel((prev) => (prev - 1 + 2) % 2);
      setHotspotActivo(null);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const toggleHotspot = (hotspotId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setHotspotActivo(prev => prev === hotspotId ? null : hotspotId);
  };

  // Funciones de zoom
  const zoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 3));
  };

  const zoomOut = () => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Zoom con rueda del ratón
  const handleWheel = (e: WheelEvent) => {
    if (!ambienteAmpliado) return;
    e.preventDefault();
    
    const delta = e.deltaY * -0.001;
    setZoom(prev => {
      const newZoom = Math.min(Math.max(prev + delta, 1), 3);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  // Drag para mover imagen con zoom
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events para móvil
  const handleTouchStart = (e: React.TouchEvent) => {
    if (zoom > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && zoom > 1 && e.touches.length === 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const imagenesCarrusel = ambienteAmpliado 
    ? [ambienteAmpliado.imagenPrincipal, ambienteAmpliado.imagenSecundaria].filter(Boolean) as string[]
    : [];

  // Keyboard navigation
  useEffect(() => {
    if (!ambienteAmpliado) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        anteriorImagen();
      } else if (e.key === 'ArrowRight') {
        siguienteImagen();
      } else if (e.key === 'Escape') {
        if (hotspotActivo) {
          setHotspotActivo(null);
        } else if (zoom > 1) {
          resetZoom();
        } else {
          cerrarAmbienteAmpliado();
        }
      } else if (e.key === '+' || e.key === '=') {
        zoomIn();
      } else if (e.key === '-') {
        zoomOut();
      } else if (e.key === '0') {
        resetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ambienteAmpliado, indiceCarrusel, hotspotActivo, zoom]);

  // Wheel event para zoom
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container || !ambienteAmpliado) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [ambienteAmpliado, zoom]);

  const ambientesMostrar = ambientesDisponibles.slice(0, ambientesMostrados);
  const hayMasAmbientes = ambientesMostrados < ambientesDisponibles.length;

  // Mostrar hotspots solo en la imagen principal (índice 0)
  const mostrarHotspots = ambienteAmpliado && indiceCarrusel === 0 && ambienteAmpliado.hotspots && zoom === 1;

  return (
    <>
      {/* Título de la sección */}
      <div className="text-center" style={{ marginBottom: 'clamp(2rem, 4vh, 4rem)', marginTop: 'clamp(3rem, 5vh, 6rem)' }}>
        <h2 
          className="font-light text-gray-900" 
          style={{ 
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(2rem, 3.5vw, 4rem)',
            marginBottom: 'clamp(0.5rem, 1vh, 1rem)'
          }}
        >
          MUEBLES DE BAÑO
        </h2>
        <p 
          className="text-gray-600 max-w-3xl mx-auto"
          style={{ 
            fontSize: 'clamp(0.875rem, 1.1vw, 1.125rem)',
            padding: '0 clamp(1rem, 2vw, 2rem)'
          }}
        >
          Explora nuestra colección completa de estilos y diseños
        </p>
      </div>

      {/* Grid de Ambientes con Lazy Loading */}
      <div 
        className="grid"
        style={{ 
          gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(200px, 20vw, 280px), 1fr))',
          gap: 'clamp(1rem, 2vw, 2rem)'
        }}
      >
        {ambientesMostrar.map((ambiente) => (
          <div 
            key={ambiente.id} 
            className="group cursor-pointer"
            onClick={() => abrirAmbienteAmpliado(ambiente)}
            onMouseEnter={() => manejarHover(ambiente.id, true)}
            onMouseLeave={() => manejarHover(ambiente.id, false)}
          >
            <div className="aspect-square bg-gray-100 overflow-hidden relative shadow-md hover:shadow-xl transition-shadow duration-300">
              <LazyImage
                src={ambiente.imagenPrincipal}
                alt={ambiente.altPrincipal}
                className={`w-full h-full object-cover transition-all duration-500 ${
                  ambiente.imagenSecundaria && imagenesHover[ambiente.id] 
                    ? 'opacity-0 scale-110' 
                    : 'opacity-100 scale-100 group-hover:scale-105'
                }`}
              />
              
              {ambiente.imagenSecundaria && imagenesHover[ambiente.id] && (
                <LazyImage
                  src={ambiente.imagenSecundaria}
                  alt={ambiente.altSecundaria || ''}
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-500 opacity-100 scale-105"
                />
              )}
              
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
              
              {ambiente.imagenSecundaria && (
                <div className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow-md">
                  <i className="ri-image-line text-gray-700" style={{ fontSize: 'clamp(0.875rem, 1vw, 1rem)' }}></i>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Botón "Cargar más" */}
      {hayMasAmbientes && (
        <div className="text-center mb-10" style={{ marginTop: 'clamp(2rem, 4vh, 4rem)' }}>
          <button
            onClick={cargarMasAmbientes}
            className="bg-gray-900 text-white hover:bg-gray-800 transition-colors cursor-pointer"
            style={{ 
              padding: 'clamp(0.875rem, 1.25vh, 1.25rem) clamp(2rem, 3vw, 3rem)',
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(0.875rem, 1vw, 1rem)',
              fontWeight: '500'
            }}
          >
            Ver más muebles de baño
          </button>
        </div>
      )}

      {/* Modal de Ambiente Ampliado con Carrusel, Hotspots y Zoom */}
      {ambienteAmpliado && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center" 
          style={{ padding: 'clamp(1rem, 2vw, 2rem)' }} 
          onClick={cerrarAmbienteAmpliado}
        >
          <div 
            className="relative w-full h-full flex items-center justify-center"
            style={{ 
              maxWidth: '95vw',
              maxHeight: '95vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón Cerrar */}
            <button
              onClick={cerrarAmbienteAmpliado}
              className="absolute z-20 bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-colors"
              style={{
                top: 'clamp(0.5rem, 2vh, 1.5rem)',
                right: 'clamp(0.5rem, 2vw, 1.5rem)',
                width: 'clamp(2.5rem, 4vw, 3.5rem)',
                height: 'clamp(2.5rem, 4vw, 3.5rem)'
              }}
            >
              <i className="ri-close-line text-gray-800" style={{ fontSize: 'clamp(1.25rem, 2vw, 1.75rem)' }}></i>
            </button>

            {/* Controles de Zoom */}
            <div className="absolute top-6 right-20 md:right-24 z-20 flex flex-col gap-2">
              <button
                onClick={zoomIn}
                disabled={zoom >= 3}
                className="bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                style={{
                  width: 'clamp(2.5rem, 3.5vw, 3rem)',
                  height: 'clamp(2.5rem, 3.5vw, 3rem)'
                }}
                title="Zoom In (+)"
              >
                <i className="ri-zoom-in-line text-gray-800" style={{ fontSize: 'clamp(1.125rem, 1.5vw, 1.375rem)' }}></i>
              </button>
              
              <button
                onClick={zoomOut}
                disabled={zoom <= 1}
                className="bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                style={{
                  width: 'clamp(2.5rem, 3.5vw, 3rem)',
                  height: 'clamp(2.5rem, 3.5vw, 3rem)'
                }}
                title="Zoom Out (-)"
              >
                <i className="ri-zoom-out-line text-gray-800" style={{ fontSize: 'clamp(1.125rem, 1.5vw, 1.375rem)' }}></i>
              </button>
              
              {zoom > 1 && (
                <button
                  onClick={resetZoom}
                  className="bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 shadow-lg"
                  style={{
                    width: 'clamp(2.5rem, 3.5vw, 3rem)',
                    height: 'clamp(2.5rem, 3.5vw, 3rem)'
                  }}
                  title="Reset Zoom (0)"
                >
                  <i className="ri-fullscreen-exit-line text-gray-800" style={{ fontSize: 'clamp(1.125rem, 1.5vw, 1.375rem)' }}></i>
                </button>
              )}
            </div>

            {/* Indicador de Zoom */}
            {zoom > 1 && (
              <div 
                className="absolute top-6 left-6 bg-black/70 text-white rounded-full px-4 py-2 text-sm font-medium z-20"
                style={{ fontSize: 'clamp(0.75rem, 1vw, 0.875rem)' }}
              >
                {Math.round(zoom * 100)}%
              </div>
            )}
            
            {/* Contenedor del Carrusel */}
            <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
              {/* Contenedor de Imagen con Hotspots y Zoom */}
              <div 
                ref={imageContainerRef}
                className="relative inline-block max-w-full max-h-full overflow-hidden"
                style={{
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  ref={imageRef}
                  src={imagenesCarrusel[indiceCarrusel]}
                  alt={indiceCarrusel === 0 ? ambienteAmpliado.altPrincipal : ambienteAmpliado.altSecundaria || ''}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl block select-none"
                  style={{
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                  }}
                  loading="eager"
                  draggable={false}
                />

                {/* Hotspots Interactivos - solo visibles sin zoom */}
                {mostrarHotspots && ambienteAmpliado.hotspots!.map((hotspot) => {
                  const esArriba = hotspot.y > 50;
                  const esIzquierda = hotspot.x > 70;
                  const esDerecha = hotspot.x < 30;
                  
                  return (
                    <div
                      key={hotspot.id}
                      className="absolute z-30"
                      style={{
                        left: `${hotspot.x}%`,
                        top: `${hotspot.y}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <button
                        onClick={(e) => toggleHotspot(hotspot.id, e)}
                        className="relative bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 shadow-lg"
                        style={{
                          width: 'clamp(2rem, 3vw, 2.5rem)',
                          height: 'clamp(2rem, 3vw, 2.5rem)'
                        }}
                      >
                        <div className="absolute inset-0 rounded-full bg-blue-500 opacity-30 animate-ping"></div>
                        <i className="ri-add-line text-gray-800 relative z-10" style={{ fontSize: 'clamp(1rem, 1.5vw, 1.25rem)' }}></i>
                      </button>

                      {hotspotActivo === hotspot.id && (
                        <div 
                          className={`absolute bg-white rounded-lg shadow-xl p-3 md:p-4 animate-fadeIn z-50 ${
                            esArriba ? 'bottom-full mb-3' : 'top-full mt-3'
                          } ${
                            esIzquierda ? 'right-0' : esDerecha ? 'left-0' : 'left-1/2 -translate-x-1/2'
                          }`}
                          style={{
                            animation: 'fadeIn 0.3s ease-in-out',
                            minWidth: 'clamp(200px, 40vw, 250px)',
                            maxWidth: 'clamp(250px, 60vw, 350px)'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div 
                            className={`absolute ${
                              esArriba ? 'top-full -mt-2' : 'bottom-full -mb-2'
                            } ${
                              esIzquierda ? 'right-4' : esDerecha ? 'left-4' : 'left-1/2 -translate-x-1/2'
                            }`}
                          >
                            {esArriba ? (
                              <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
                            ) : (
                              <div className="w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white"></div>
                            )}
                          </div>

                          <div className="text-left">
                            <h4 
                              className="font-semibold text-gray-900 mb-1.5"
                              style={{ 
                                fontFamily: 'Playfair Display, serif',
                                fontSize: 'clamp(0.9375rem, 1.2vw, 1.125rem)'
                              }}
                            >
                              {hotspot.titulo}
                            </h4>
                            {hotspot.descripcion && (
                              <p 
                                className="text-gray-600 text-sm"
                                style={{ fontSize: 'clamp(0.8125rem, 1vw, 0.9375rem)' }}
                              >
                                {hotspot.descripcion}
                              </p>
                            )}
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setHotspotActivo(null);
                            }}
                            className="absolute top-1.5 right-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <i className="ri-close-line" style={{ fontSize: 'clamp(1rem, 1.2vw, 1.125rem)' }}></i>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Botones de Navegación del Carrusel */}
              {ambienteAmpliado.imagenSecundaria && (
                <>
                  <button
                    onClick={anteriorImagen}
                    className="absolute left-4 z-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110"
                    style={{
                      width: 'clamp(2.5rem, 4vw, 3.5rem)',
                      height: 'clamp(2.5rem, 4vw, 3.5rem)'
                    }}
                  >
                    <i className="ri-arrow-left-s-line text-gray-800" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}></i>
                  </button>
                  
                  <button
                    onClick={siguienteImagen}
                    className="absolute right-4 z-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110"
                    style={{
                      width: 'clamp(2.5rem, 4vw, 3.5rem)',
                      height: 'clamp(2.5rem, 4vw, 3.5rem)'
                    }}
                  >
                    <i className="ri-arrow-right-s-line text-gray-800" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}></i>
                  </button>

                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                    {imagenesCarrusel.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setIndiceCarrusel(index);
                          setHotspotActivo(null);
                          resetZoom();
                        }}
                        className={`rounded-full transition-all cursor-pointer ${
                          index === indiceCarrusel 
                            ? 'bg-white w-8 h-3' 
                            : 'bg-white/50 hover:bg-white/75 w-3 h-3'
                        }`}
                        aria-label={`Ir a imagen ${index + 1}`}
                      />
                    ))}
                  </div>

                  <div 
                    className="absolute top-6 left-6 bg-black/60 text-white rounded-full px-4 py-2 text-sm font-medium"
                    style={{ fontSize: 'clamp(0.75rem, 1vw, 0.875rem)' }}
                  >
                    {indiceCarrusel + 1} / {imagenesCarrusel.length}
                  </div>
                </>
              )}

              {/* Leyenda del Ambiente - solo visible sin zoom */}
              {ambienteAmpliado.leyenda && indiceCarrusel === 0 && zoom === 1 && (
                <div 
                  className="absolute bottom-6 left-6 bg-black/20 backdrop-blur-sm rounded-lg shadow-2xl p-4 md:p-5 z-10"
                  style={{
                    maxWidth: 'clamp(280px, 90vw, 450px)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 
                    className="font-semibold text-white mb-1.5 md:mb-2 drop-shadow-lg"
                    style={{ 
                      fontFamily: 'Playfair Display, serif',
                      fontSize: 'clamp(1rem, 1.5vw, 1.375rem)',
                      textShadow: '0 2px 4px rgba(0,0,0,0.8)'
                    }}
                  >
                    {ambienteAmpliado.leyenda.titulo}
                  </h3>

                  <p 
                    className="text-white mb-2 md:mb-3 leading-relaxed drop-shadow-md"
                    style={{ 
                      fontSize: 'clamp(0.8125rem, 1vw, 0.9375rem)',
                      textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                    }}
                  >
                    {ambienteAmpliado.leyenda.descripcion}
                  </p>

                  {ambienteAmpliado.leyenda.caracteristicas && (
                    <div className="border-t border-white/30 pt-2 md:pt-3">
                      <ul className="space-y-1 md:space-y-1.5">
                        {ambienteAmpliado.leyenda.caracteristicas.map((caract, index) => (
                          <li 
                            key={index}
                            className="text-white flex items-start drop-shadow-md"
                            style={{ 
                              fontSize: 'clamp(0.75rem, 0.95vw, 0.875rem)',
                              textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                            }}
                          >
                            <i className="ri-check-line text-green-400 mr-1.5 md:mr-2 mt-0.5 flex-shrink-0 text-sm drop-shadow-lg"></i>
                            <span>{caract}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="absolute top-2 md:top-3 right-2 md:right-3 text-white/60 drop-shadow-lg">
                    <i className="ri-information-line" style={{ fontSize: 'clamp(1.125rem, 1.5vw, 1.5rem)' }}></i>
                  </div>
                </div>
              )}
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateX(-50%) translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateX(-50%) translateY(0);
              }
            }
          `}</style>
        </div>
      )}
    </>
  );
}